import { $api } from './interceptor';
import { Paginated } from 'types/paginated';
import { AxiosError } from 'axios';
import { toast } from 'App';
import { IToken, TutorialStatusEnum, TVideoToken } from 'views/types/token';
import { videoTokensStore } from 'views/store/useVideoTokensStore';

const VideoTokensService = () => {
  const getVideoTokens = async ({
    page = 0,
    pageSize = 10,
    searchValue,
    sortingValue,
  }: {
    page?: number;
    pageSize?: number;
    searchValue?: string;
    sortingValue: string;
  }): Promise<Paginated<TVideoToken>> => {
    let search = searchValue;

    try {
      const { data } = await $api.get<Paginated<TVideoToken>>(
        'api/admins/video-tokens',
        {
          params: {
            search,
            page: page + 1,
            limit: pageSize,
            sortBy: sortingValue,
          },
        }
      );

      videoTokensStore.setState({
        tokens: data.data,
        totalPages: data.meta.totalPages,
        totalItems: data.meta.totalItems,
      });
      if (!search) {
        videoTokensStore.setState({
          totalTokens: data.meta.totalItems,
        });
      }
      return data;
    } catch (e) {
      console.log(e);
      if (e instanceof AxiosError) {
        toast({
          title: 'Error',
          description: e.response?.data.message,
          status: 'error',
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Error fetching user list',
          status: 'error',
          isClosable: true,
          position: 'top-right',
        });
      }
      throw e;
    }
  };

  const deleteVideoToken = async (data: {
    tokenId: string;
    creatorId: number;
    reason: string;
    tokenSymbol: string;
  }) => {
    try {
      await $api.post('/api/admins/video-token', {
        ...data,
      });
      toast({
        title: 'Success',
        description: `${data.tokenSymbol} was successful deleted`,
        status: 'success',
        position: 'top-right',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: `Error while try to delete token ${data.tokenSymbol}`,
        status: 'error',
        position: 'top-right',
      });
    }
  };

  const setTutorial = async (
    data: {
      tokenAddress: string;
      tutorialStatus: TutorialStatusEnum;
      reserveOrder?: number;
    },
    withToast = true
  ) => {
    try {
      await $api.post('/api/admins/tutorial', {
        ...data,
      });
      withToast &&
        toast({
          title: 'Success',
          description: `Tutorial token added`,
          status: 'success',
          position: 'top-right',
        });
    } catch (error) {
      if (error instanceof AxiosError) {
        withToast &&
          toast({
            title: 'Error',
            description:
              error.response.data?.message ?? 'Failed add tutorial token',
            status: 'error',
            position: 'top-right',
          });
      } else {
        withToast &&
          toast({
            title: 'Error',
            description: `Failed add tutorial token`,
            status: 'error',
            position: 'top-right',
          });
      }
    }
  };

  const getTutorialTokens = async () => {
    try {
      const { data } = await $api.get<IToken[]>(
        `/api/admins/tutorial?status=${TutorialStatusEnum.ACTIVE}&status=${TutorialStatusEnum.ENDED}&status=${TutorialStatusEnum.RESERVE}`
      );

      return data;
    } catch (error) {
      if (error instanceof AxiosError) {
        toast({
          title: 'Error',
          description:
            error.response.data?.message ??
            'Failed get tutorial tokens, please try again',
          status: 'error',
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed get tutorial tokens, please try again',
          status: 'error',
          position: 'top-right',
        });
      }
      return [];
    }
  };

  return { getVideoTokens, deleteVideoToken, setTutorial, getTutorialTokens };
};

export const videoTokenService = VideoTokensService();
